import Information16 from '@carbon/icons-react/es/information/16';
import { useBoolean } from 'ahooks';
import { Col, Form, Row, Tooltip } from 'antd';
import { duration } from 'moment';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { LinkButton } from 'components/atoms/button';
import { InputNumber } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { removeInsignificantTrailingZero } from 'components/organisms/resource-management/utils/dates';
import { useTranslate } from 'hooks/use-translation';
import { useCompanyUpdateMutation } from 'queries';
import {
  ACCOUNT_META_DATA_QUERY_KEY,
  ACCOUNT_PROFILE_KEY,
  useAccountProfile,
} from 'queries/account';
import { ALL_MEMBERS_QUERY_KEY, useNativeMembersQuery } from 'queries/members';
import { SettingsLabel } from '../styles';
import { Container, Content, FormWithMarginTop } from './styled';

const StyledInfoIcon = styled(Information16)`
  margin-left: var(--spacing-02);
`;

export const WeeklyTeamCapacity: FC = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const translate = useTranslate();

  /**
   * Consuming members here even though it is not needed
   * This is because invalidation is not working otherwise
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: members } = useNativeMembersQuery();
  const { data: account } = useAccountProfile();
  const { mutateAsync: updateCompany } = useCompanyUpdateMutation(
    account?.company.companyId ?? -1, // TODO: Need to handle this better
  );

  const [isModalVisible, { setTrue: showModal, setFalse: hideModal }] =
    useBoolean(false);
  const [isLoading, { setTrue: showLoading, setFalse: hideLoading }] =
    useBoolean(false);

  const inputPlaceholder = translate(
    'settings.organisation.capacity.modal.input.placeholder',
  );
  const inputError = translate('settings.weekly_capacity.input.error');

  const onSave = useCallback(async () => {
    try {
      showLoading();
      await form.validateFields();
      await updateCompany({ capacity: form.getFieldValue('capacity') });
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      await queryClient.invalidateQueries(ACCOUNT_PROFILE_KEY);
      await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      hideLoading();

      form.resetFields();
      hideModal();

      toastService({
        title: translate('settings.organisation.capacity.toast.success.title'),
        message: translate(
          'settings.organisation.capacity.toast.success.message',
          {
            hours: form.getFieldValue('capacity'),
          },
        ),
      }).success();
    } catch (e) {
      toastService({
        title: translate('settings.organisation.capacity.toast.failure.title'),
        message: translate(
          'settings.organisation.capacity.toast.failure.message',
          {
            hours: form.getFieldValue('capacity'),
          },
        ),
      }).error();
    }
  }, [
    form,
    hideLoading,
    hideModal,
    queryClient,
    showLoading,
    translate,
    updateCompany,
  ]);
  const onCancel = useCallback(() => {
    form.resetFields();
    hideModal();
  }, [form, hideModal]);

  if (!account) {
    return <></>;
  }

  return (
    <Container>
      <SettingsLabel>
        <FormattedMessage id="settings.organisation.capacity.title" />
        <Tooltip
          title={
            <FormattedMessage id="settings.organisation.capacity.tooltip" />
          }
        >
          <StyledInfoIcon />
        </Tooltip>
      </SettingsLabel>
      <Content>
        <div>
          <FormattedMessage
            id="settings.organisation.capacity.capacity"
            values={{ hours: account.company.capacity }}
          />
        </div>
        <LinkButton onClick={showModal}>
          <FormattedMessage id="placeholder.change" />
        </LinkButton>
        <Modal
          title={
            <FormattedMessage id="settings.organisation.capacity.modal.title" />
          }
          visible={isModalVisible}
          onOk={onSave}
          onCancel={onCancel}
          okButtonProps={{ loading: isLoading }}
          okText={<FormattedMessage id="save" />}
        >
          <FormWithMarginTop
            form={form}
            layout="vertical"
            initialValues={{ capacity: account.company.capacity }}
          >
            <Row gutter={16}>
              <Col span="24">
                <Form.Item
                  name="capacity"
                  label={
                    <FormattedMessage id="settings.organisation.capacity.modal.input.label" />
                  }
                  rules={[
                    () => ({
                      validator(_, value) {
                        const x = Number(
                          removeInsignificantTrailingZero(value),
                        );

                        if (x <= 0 || x > duration(24, 'hours').asHours() * 7) {
                          return Promise.reject(new Error(inputError));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    autoFocus
                    placeholder={inputPlaceholder}
                    formatter={(value) => `${value}h`}
                    parser={(value) => value?.replace('h', '') ?? ''}
                    onBlur={() => {
                      form.setFieldsValue({
                        capacity: Number(
                          removeInsignificantTrailingZero(
                            form.getFieldValue('capacity'),
                          ),
                        ),
                      });
                      form.validateFields(['capacity']);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormWithMarginTop>
        </Modal>
      </Content>
    </Container>
  );
};
